import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/calendar';
import { LoadingSkeleton } from '@/components/ui/loading-spinner';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ContractTemplate, Contract } from '@/types';
import { FileText, User, Calendar, DollarSign } from 'lucide-react';

interface JobApplication {
  id: string;
  job_posting_id: string;
  applicant_name: string;
  applicant_email: string;
  application_data: any;
}

interface ContractGenerationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  jobApplication: JobApplication | null;
  businessId: string;
  jobPosting?: {
    title: string;
    employment_type: string;
    default_contract_template_id?: string;
    contract_auto_generate?: boolean;
  };
}

interface ContractGenerationData {
  template_id: string;
  start_date: Date | null;
  salary_amount: string;
  hourly_rate: string;
  probation_period_days: string;
  work_location: string;
  job_title: string;
  notes: string;
}

const ContractGenerationDialog: React.FC<ContractGenerationDialogProps> = ({
  isOpen,
  onClose,
  jobApplication,
  businessId,
  jobPosting
}) => {
  const [contractTemplates, setContractTemplates] = useState<ContractTemplate[]>([]);
  const [loading, setLoading] = useState({
    templates: false,
    generating: false
  });
  
  const [formData, setFormData] = useState<ContractGenerationData>({
    template_id: jobPosting?.default_contract_template_id || '',
    start_date: null,
    salary_amount: '',
    hourly_rate: '',
    probation_period_days: '90',
    work_location: '',
    job_title: jobPosting?.title || '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchContractTemplates();
    }
  }, [isOpen, businessId, jobPosting?.employment_type]);

  const fetchContractTemplates = async () => {
    setLoading(prev => ({ ...prev, templates: true }));
    try {
      const { data, error } = await supabase
        .from('contract_templates')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setContractTemplates(data || []);

      // Auto-select template if job posting has a default
      if (jobPosting?.default_contract_template_id && data) {
        const defaultTemplate = data.find(t => t.id === jobPosting.default_contract_template_id);
        if (defaultTemplate) {
          setFormData(prev => ({ ...prev, template_id: defaultTemplate.id }));
        }
      }
    } catch (error) {
      console.error('Error fetching contract templates:', error);
      toast.error('Failed to load contract templates');
    } finally {
      setLoading(prev => ({ ...prev, templates: false }));
    }
  };

  const handleGenerateContract = async () => {
    if (!jobApplication || !formData.template_id || !formData.start_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(prev => ({ ...prev, generating: true }));

    try {
      // Create employee record first
      const { data: employee, error: employeeError } = await supabase
        .from('employees')
        .insert({
          business_id: businessId,
          name: jobApplication.applicant_name,
          email: jobApplication.applicant_email,
          role_id: null, // Will be set later
          employment_status: 'pending',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (employeeError) throw employeeError;

      // Generate contract
      const contractData = {
        business_id: businessId,
        employee_id: employee.id,
        template_id: formData.template_id,
        status: 'draft',
        contract_type: jobPosting?.employment_type || 'full-time',
        start_date: formData.start_date.toISOString(),
        salary_amount: formData.salary_amount ? parseFloat(formData.salary_amount) : null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        probation_period_days: formData.probation_period_days ? parseInt(formData.probation_period_days) : null,
        work_location: formData.work_location || null,
        job_title: formData.job_title,
        notes: formData.notes || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: contract, error: contractError } = await supabase
        .from('contracts')
        .insert(contractData)
        .select()
        .single();

      if (contractError) throw contractError;

      // Update job application status
      await supabase
        .from('job_applications')
        .update({ 
          status: 'hired',
          hired_at: new Date().toISOString(),
          employee_id: employee.id
        })
        .eq('id', jobApplication.id);

      toast.success('Contract generated successfully! Employee record created.');
      onClose();

      // Reset form
      setFormData({
        template_id: jobPosting?.default_contract_template_id || '',
        start_date: null,
        salary_amount: '',
        hourly_rate: '',
        probation_period_days: '90',
        work_location: '',
        job_title: jobPosting?.title || '',
        notes: ''
      });

    } catch (error: any) {
      console.error('Error generating contract:', error);
      toast.error(`Failed to generate contract: ${error.message}`);
    } finally {
      setLoading(prev => ({ ...prev, generating: false }));
    }
  };

  if (!jobApplication) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generate Employment Contract
          </DialogTitle>
          <DialogDescription>
            Create a contract for {jobApplication.applicant_name} based on their job application for {jobPosting?.title}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Applicant Information */}
          <div className="bg-muted p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4" />
              <h4 className="font-semibold">Applicant Information</h4>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Name:</span> {jobApplication.applicant_name}
              </div>
              <div>
                <span className="font-medium">Email:</span> {jobApplication.applicant_email}
              </div>
            </div>
          </div>

          {/* Contract Template Selection */}
          <div>
            <Label htmlFor="template">Contract Template *</Label>
            {loading.templates ? (
              <LoadingSkeleton variant="input" />
            ) : (
              <Select
                value={formData.template_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, template_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a contract template" />
                </SelectTrigger>
                <SelectContent>
                  {contractTemplates
                    .filter(template => 
                      !jobPosting?.employment_type || 
                      template.employment_type === jobPosting.employment_type ||
                      template.employment_type === 'any'
                    )
                    .map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name} ({template.employment_type})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Contract Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="job_title">Job Title *</Label>
              <Input
                id="job_title"
                value={formData.job_title}
                onChange={(e) => setFormData(prev => ({ ...prev, job_title: e.target.value }))}
                placeholder="e.g., Sales Associate"
              />
            </div>
            <div>
              <Label htmlFor="start_date">Start Date *</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date ? formData.start_date.toISOString().split('T')[0] : ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  start_date: e.target.value ? new Date(e.target.value) : null 
                }))}
              />
            </div>
          </div>

          {/* Compensation */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <h4 className="font-semibold">Compensation</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="salary_amount">Annual Salary</Label>
                <Input
                  id="salary_amount"
                  type="number"
                  value={formData.salary_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, salary_amount: e.target.value }))}
                  placeholder="50000"
                />
              </div>
              <div>
                <Label htmlFor="hourly_rate">Hourly Rate</Label>
                <Input
                  id="hourly_rate"
                  type="number"
                  step="0.01"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                  placeholder="25.00"
                />
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="probation_period">Probation Period (days)</Label>
              <Input
                id="probation_period"
                type="number"
                value={formData.probation_period_days}
                onChange={(e) => setFormData(prev => ({ ...prev, probation_period_days: e.target.value }))}
                placeholder="90"
              />
            </div>
            <div>
              <Label htmlFor="work_location">Work Location</Label>
              <Input
                id="work_location"
                value={formData.work_location}
                onChange={(e) => setFormData(prev => ({ ...prev, work_location: e.target.value }))}
                placeholder="Main Office, Remote, etc."
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional terms or notes for this contract..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleGenerateContract}
            disabled={loading.generating || !formData.template_id || !formData.start_date}
          >
            {loading.generating ? 'Generating...' : 'Generate Contract & Create Employee'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContractGenerationDialog;